import React, { useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Text, TextInput } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import auth from '../lib/auth';
import { AuthError } from '../lib/auth/types';
import { activeCriteria, checkPassword, isPasswordValid } from '../lib/auth/password';
import ModalSheet from './ModalSheet';

type Mode = 'signin' | 'signup';

interface AuthModalProps {
  visible: boolean;
  onDismiss: () => void;
}

const EMAIL_RE = /\S+@\S+\.\S+/;

const AuthModal: React.FC<AuthModalProps> = ({ visible, onDismiss }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const clearMessages = () => {
    setError(null);
    setInfo(null);
  };

  const unmet = checkPassword(password);
  const emailValid = EMAIL_RE.test(email);
  // On sign-up the password must satisfy the policy; on sign-in we only need a
  // non-empty value (an existing password may predate the current policy).
  const canSubmit =
    !loading && emailValid && (mode === 'signin' ? password.length > 0 : isPasswordValid(password));

  const showError = (e: unknown) => {
    const code = e instanceof AuthError ? e.code : 'unknown';
    setError(t([`auth.errors.${code}`, 'auth.errors.unknown']));
    track('auth_error', { code });
  };

  const submit = async () => {
    clearMessages();
    setLoading(true);
    try {
      if (mode === 'signin') {
        await auth.signInWithEmail(email.trim(), password);
        track('sign_in', { method: 'password' });
      } else {
        await auth.signUpWithEmail(email.trim(), password);
        track('sign_up', { method: 'password' });
      }
      setPassword('');
      onDismiss();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    clearMessages();
    if (!emailValid) {
      setError(t('auth.enterEmailFirst'));
      return;
    }
    setLoading(true);
    try {
      await auth.sendPasswordReset(email.trim());
      track('password_reset');
      setInfo(t('auth.resetSent'));
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const social = async (provider: 'google' | 'apple') => {
    clearMessages();
    setLoading(true);
    try {
      if (provider === 'google') await auth.signInWithGoogle();
      else await auth.signInWithApple();
      track('sign_in', { method: provider });
      onDismiss();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const continueAnonymous = async () => {
    clearMessages();
    setLoading(true);
    try {
      await auth.signInAnonymously();
      track('sign_in', { method: 'anonymous' });
      onDismiss();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
    clearMessages();
  };

  return (
    <ModalSheet visible={visible} onDismiss={onDismiss} cardStyle={styles.card}>
      <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
        <Text style={styles.title}>
          {t(mode === 'signin' ? 'auth.title.signIn' : 'auth.title.signUp')}
        </Text>

        <Button
          mode="outlined"
          icon="google"
          onPress={() => social('google')}
          disabled={loading}
          textColor={palette.textPrimary}
          style={styles.social}
        >
          {t('auth.continueGoogle')}
        </Button>
        {/* Apple Sign In is iOS/web only (Android has no Apple flow). */}
        {Platform.OS !== 'android' && (
          <Button
            mode="outlined"
            icon="apple"
            onPress={() => social('apple')}
            disabled={loading}
            textColor={palette.textPrimary}
            style={styles.social}
          >
            {t('auth.continueApple')}
          </Button>
        )}

        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>{t('auth.or')}</Text>
          <View style={styles.dividerLine} />
        </View>

        <TextInput
          mode="outlined"
          label={t('auth.email')}
          value={email}
          onChangeText={(v) => {
            setEmail(v);
            clearMessages();
          }}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          textContentType="emailAddress"
          style={styles.input}
        />

        <TextInput
          mode="outlined"
          label={t('auth.password')}
          value={password}
          onChangeText={(v) => {
            setPassword(v);
            clearMessages();
          }}
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoCorrect={false}
          textContentType={mode === 'signin' ? 'password' : 'newPassword'}
          right={
            <TextInput.Icon
              icon={showPassword ? 'eye-off' : 'eye'}
              onPress={() => setShowPassword((s) => !s)}
              accessibilityLabel={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
            />
          }
          style={styles.input}
        />

        {mode === 'signup' && password.length > 0 && (
          <View style={styles.criteria}>
            {activeCriteria().map((c) => {
              const met = !unmet.includes(c);
              return (
                <View key={c} style={styles.criterionRow}>
                  <MaterialCommunityIcons
                    name={met ? 'check-circle' : 'circle-outline'}
                    size={16}
                    color={met ? palette.appliedText : palette.textMuted}
                  />
                  <Text
                    style={[
                      styles.criterionText,
                      { color: met ? palette.textPrimary : palette.textSecondary },
                    ]}
                  >
                    {t(`auth.criteria.${c}`)}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {mode === 'signin' && (
          <Button
            mode="text"
            compact
            onPress={forgotPassword}
            disabled={loading}
            textColor={palette.secondary}
            style={styles.forgot}
          >
            {t('auth.forgotPassword')}
          </Button>
        )}

        {error ? <Text style={styles.error}>{error}</Text> : null}
        {info ? <Text style={styles.info}>{info}</Text> : null}

        <Button
          mode="contained"
          onPress={submit}
          disabled={!canSubmit}
          loading={loading}
          buttonColor={palette.primary}
          // Intentionally always-dark text on the green primary CTA — same
          // convention as SettingsModal / NativeAdCard.
          textColor="#0F1115"
          style={styles.submit}
          contentStyle={styles.submitContent}
        >
          {t(mode === 'signin' ? 'auth.signIn' : 'auth.signUp')}
        </Button>

        <Button mode="text" onPress={toggleMode} textColor={palette.textSecondary}>
          {t(mode === 'signin' ? 'auth.noAccount' : 'auth.haveAccount')}
        </Button>

        <View style={styles.divider}>
          <View style={styles.dividerLine} />
        </View>
        <Button
          mode="text"
          icon="incognito"
          onPress={continueAnonymous}
          disabled={loading}
          textColor={palette.textSecondary}
        >
          {t('auth.continueAnonymous')}
        </Button>
      </ScrollView>
    </ModalSheet>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: { backgroundColor: p.surface, borderRadius: radius.lg },
    body: { padding: spacing.lg, gap: spacing.sm },
    title: {
      fontSize: 20,
      fontWeight: '700',
      color: p.textPrimary,
      marginBottom: spacing.sm,
    },
    social: { borderRadius: radius.pill, borderColor: p.border },
    divider: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginVertical: spacing.xs },
    dividerLine: { flex: 1, height: StyleSheet.hairlineWidth, backgroundColor: p.border },
    dividerText: { color: p.textSecondary, fontSize: 12 },
    input: { backgroundColor: p.surface },
    criteria: {
      gap: spacing.xs,
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.xs,
    },
    criterionRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    criterionText: { fontSize: 13 },
    forgot: { alignSelf: 'flex-start' },
    error: { color: p.danger, fontSize: 13, marginTop: spacing.xs },
    info: { color: p.appliedText, fontSize: 13, marginTop: spacing.xs },
    submit: { borderRadius: radius.pill, marginTop: spacing.sm },
    submitContent: { minHeight: 44 },
  });

export default AuthModal;
